import { formatProvinces } from '../../../../utils/locationOptions';

const SERVICE_DEFINITIONS = [
    { key: 'assessments', customerKey: 'assessment', label: 'Assessments' },
    { key: 'courses', customerKey: 'course', label: 'Courses' },
    { key: 'apprenticeships', customerKey: 'apprenticeship', label: 'Apprenticeships' },
    { key: 'labs', customerKey: 'lab', label: 'Labs' },
];

const asNumber = value => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const asArray = value => Array.isArray(value) ? value : [];

const programmeLabel = project => {
    const type = project?.programme_type_label || '';
    if (!type) return 'Not set';
    return project?.programme_subtype_label ? `${type} · ${project.programme_subtype_label}` : type;
};

const sumField = (items, field) => asArray(items).reduce((total, item) => (
    total + asNumber(item?.[field])
), 0);

const looksLikeUuid = value => (
    typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
);

const administratorName = value => {
    if (value && typeof value === 'object') {
        return value.userName || value.name || value.email || '';
    }
    if (typeof value === 'string' && value.trim() && !looksLikeUuid(value.trim())) {
        return value.trim();
    }
    return '';
};

export const projectAdministratorLabel = (project = {}, summary = {}) => {
    const directName = [
        project.project_admin_name,
        project.project_administrator_name,
        project.admin_name,
        project.project_admin,
        summary.project_admin,
    ].map(administratorName).find(Boolean);

    if (directName) return directName;

    const administrators = asArray(project.project_admins || project.administrators)
        .map(administratorName)
        .filter(Boolean);
    if (administrators.length) return administrators.join(', ');

    const count = asNumber(project.admin_count);
    if (count > 0) return `${count} administrator${count === 1 ? '' : 's'} assigned`;
    return 'Not recorded';
};

export const buildOnboardingReview = ({
    customer,
    customerAdmins,
    customerLicences,
    projectResults,
}) => {
    const projects = asArray(projectResults);
    let totalUsers = 0;

    const projectCards = projects.map(({ project = {}, summary = {}, licences = {} }) => {
        const projectUsers = asNumber(
            project.total_users
            ?? project.total_users_count
            ?? summary.total_users_count
        );
        totalUsers += projectUsers;

        const assignedLicences = SERVICE_DEFINITIONS.reduce((total, service) => (
            total + sumField(licences?.[service.key], 'user_count')
        ), 0);

        return {
            id: project.project_uuid,
            name: project.project_name || summary.project_name || 'Untitled project',
            administrator: projectAdministratorLabel(project, summary),
            programme: programmeLabel(project),
            users: projectUsers,
            assignedLicences,
        };
    });

    const serviceRows = SERVICE_DEFINITIONS.map(service => {
        const customerRows = customerLicences?.[service.customerKey];
        const assigned = projects.reduce((total, project) => (
            total + sumField(project.licences?.[service.key], 'user_count')
        ), 0);

        return {
            key: service.key,
            label: service.label,
            provisioned: sumField(customerRows, 'total_count'),
            allocated: sumField(customerRows, 'count'),
            assigned,
        };
    });

    const totals = serviceRows.reduce((result, row) => ({
        provisioned: result.provisioned + row.provisioned,
        allocated: result.allocated + row.allocated,
        assigned: result.assigned + row.assigned,
    }), { provisioned: 0, allocated: 0, assigned: 0 });

    const unassignedByProject = [];
    projects.forEach(({ project = {}, summary = {}, licences = {} }) => {
        SERVICE_DEFINITIONS.forEach(service => {
            const remaining = asArray(licences?.[service.key]).reduce((total, item) => (
                total + Math.max(asNumber(item?.left_count) - asNumber(item?.user_count), 0)
            ), 0);
            if (remaining > 0) {
                unassignedByProject.push({
                    projectName: project.project_name || summary.project_name || 'Untitled project',
                    service: service.label,
                    count: remaining,
                });
            }
        });
    });

    return {
        customer: {
            name: customer?.name || 'Not recorded',
            province: formatProvinces(customer, 'Not recorded'),
            administrators: asArray(customerAdmins).map(admin => admin?.userName || admin?.name).filter(Boolean),
        },
        projectCards,
        serviceRows,
        totals,
        serviceCount: serviceRows.filter(row => row.provisioned > 0).length,
        uniqueUserCount: totalUsers,
        allProjectsHaveAdministrator: projectCards.length > 0
            && projectCards.every(project => project.administrator !== 'Not recorded'),
        clientPoolRemaining: Math.max(totals.provisioned - totals.allocated, 0),
        projectLicencesRemaining: Math.max(totals.allocated - totals.assigned, 0),
        unassignedByProject,
    };
};

export const loadOnboardingReview = async ({ customerId, customer, projectServices }) => {
    const [adminResponse, licenceResponse, projectsResponse] = await Promise.all([
        projectServices.getCustomerAdminList({ customer: customerId }),
        projectServices.getProjectDetails(customerId),
        projectServices.allProjectsByCustomer(customerId),
    ]);

    const projects = projectsResponse?.results || [];
    const projectResults = await Promise.all(projects.map(async project => {
        const projectLicenceResponse = await projectServices.getDetailLicenseCustomer(project.project_uuid);
        return {
            project,
            licences: projectLicenceResponse?.data || {},
        };
    }));

    return buildOnboardingReview({
        customer,
        customerAdmins: adminResponse?.data || [],
        customerLicences: licenceResponse?.data || licenceResponse || {},
        projectResults,
    });
};
